import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

type Subscriber = () => void;
type UnsubscribeFunction = () => void;
type NotifyFunction = () => void;

interface TourTriggerContextIf {
  enabled: boolean;
  notify: NotifyFunction;
  register: (subscriber: Subscriber) => UnsubscribeFunction;
}

const TourTriggerContext = createContext<TourTriggerContextIf>({
  enabled: false,
  notify: () => null,
  register: () => () => null,
});

function useTourTriggerContextProvider() {
  const [subscribers, setSubscribers] = useState<Subscriber[]>([]);

  // Functional updates — the previous implementation closed over a stale
  // `subscribers` array, dropping concurrent (un)registrations.
  const register = useCallback((subscriber: Subscriber) => {
    setSubscribers(prev => [...new Set([...prev, subscriber])]);
    return () =>
      setSubscribers(prev => prev.filter(item => item !== subscriber));
  }, []);

  const notify = useCallback(
    () => subscribers.forEach(subscriber => subscriber()),
    [subscribers],
  );

  const context = useMemo(
    () => ({
      enabled: !!subscribers.length,
      notify,
      register,
    }),
    [subscribers.length, notify, register],
  );

  return { context };
}

interface TourTriggerContextProviderProps {
  children: ReactNode;
}

export function TourTriggerContextProvider({
  children,
}: TourTriggerContextProviderProps) {
  const { context } = useTourTriggerContextProvider();

  return (
    <TourTriggerContext.Provider value={context}>
      {children}
    </TourTriggerContext.Provider>
  );
}

export function useTourTriggerContext() {
  return useContext(TourTriggerContext);
}
